import { useMemo, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { ProductCard } from "./ProductCard";
import { motion } from "framer-motion";

const BRANDS = ["Todas", "Nike", "Jordan", "Adidas", "New Balance", "On", "Mizuno", "Outros"];
const GENDERS = [
  { id: "todos", label: "Todos" },
  { id: "masculino", label: "Masculino" },
  { id: "feminino", label: "Feminino" },
];
const SIZES = [35, 36, 37, 38, 39, 40, 41, 42, 43, 44];
const SORTS = [
  { id: "populares", label: "Populares" },
  { id: "menor", label: "Menor preço" },
  { id: "maior", label: "Maior preço" },
  { id: "lancamentos", label: "Lançamentos" },
];

export function Catalog() {
  const { products, loading, categoryFilter, setCategoryFilter } = useStore();
  const [brand, setBrand] = useState("Todas");
  const [gender, setGender] = useState("todos");
  const [search, setSearch] = useState("");
  const [size, setSize] = useState(null);
  const [sort, setSort] = useState("populares");

  const filtered = useMemo(() => {
    let list = [...products];
    if (categoryFilter) {
      const target = categoryFilter.trim().toLowerCase();
      list = list.filter((p) =>
        (p.category || "").trim().toLowerCase() === target ||
        (p.badge || "").trim().toLowerCase() === target
      );
    }
    if (brand !== "Todas") list = list.filter((p) => p.brand === brand);
    if (gender !== "todos") {
      const target = gender === "masculino" ? "Masculino" : "Feminino";
      list = list.filter((p) => ["Unissex", target].includes(p.gender || "Unissex"));
    }
    if (search) list = list.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
    if (size) list = list.filter((p) => p.sizes.includes(size));
    switch (sort) {
      case "menor": list.sort((a, b) => a.price - b.price); break;
      case "maior": list.sort((a, b) => b.price - a.price); break;
      case "lancamentos": list.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0)); break;
      default: list.sort((a, b) => b.reviews_count - a.reviews_count);
    }
    return list;
  }, [products, brand, gender, search, size, sort, categoryFilter]);

  return (
    <section id="catalogo" data-testid="vult-catalog-section" className="bg-[#F8F9FA] py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="font-mono text-xs font-bold uppercase tracking-widest text-vult-red">Catálogo completo</div>
          <h2 className="mt-2 font-display text-4xl font-black uppercase leading-none tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Escolha seu <span className="bg-vult-lime px-2 italic">par</span>
          </h2>
        </motion.div>

        <div className="mb-8 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {BRANDS.map((b) => (
              <button
                key={b}
                data-testid={`vult-brand-tab-filter-${b.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setBrand(b)}
                className={`rounded-full px-5 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-all ${
                  brand === b ? "bg-slate-900 text-white shadow-[3px_3px_0_#FFD600]" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-900"
                }`}
              >
                {b}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-xs">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="vult-catalog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar modelo..."
                className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-slate-900"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Gênero</span>
              {GENDERS.map((g) => (
                <button
                  key={g.id}
                  data-testid={`vult-gender-filter-${g.id}`}
                  onClick={() => setGender(g.id)}
                  className={`rounded-full px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-all ${
                    gender === g.id ? "bg-vult-blue text-white shadow-[3px_3px_0_#0F172A]" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-900"
                  }`}
                >
                  {g.label}
                </button>
              ))}
              <span className="mx-1 hidden h-5 w-px bg-slate-300 sm:block" />
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Tam BR</span>
              {SIZES.map((s) => (
                <button
                  key={s}
                  data-testid={`vult-size-pill-filter-${s}`}
                  onClick={() => setSize(size === s ? null : s)}
                  className={`h-9 w-9 rounded-full font-mono text-xs font-bold transition-all ${
                    size === s ? "bg-vult-red text-slate-900" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-900"
                  }`}
                >
                  {s}
                </button>
              ))}
              <div className="relative ml-1 flex items-center gap-1">
                <SlidersHorizontal size={14} className="text-slate-400" />
                <select
                  data-testid="vult-catalog-sort-select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="rounded-full border border-slate-300 bg-white px-3 py-2 font-mono text-xs font-bold uppercase tracking-wider outline-none"
                >
                  {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {categoryFilter && (
            <div>
              <button
                data-testid="vult-catalog-category-chip"
                onClick={() => setCategoryFilter(null)}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-white shadow-[3px_3px_0_#FFD600] transition-colors hover:bg-vult-red hover:text-slate-900"
              >
                Categoria: {categoryFilter} <X size={13} />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-72 animate-pulse rounded-3xl bg-slate-200 sm:h-96" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="vult-catalog-empty" className="rounded-3xl border-2 border-dashed border-slate-300 py-20 text-center">
            <div className="font-display text-2xl font-black uppercase text-slate-400">Nenhum par encontrado</div>
            <p className="mt-1 text-sm text-slate-500">Ajuste os filtros ou limpe a busca.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}
